import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { xIcon } from "@progress/kendo-svg-icons";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import loadinggif from "../../assets/loading.gif";
import { process } from "@progress/kendo-data-query";
import { useProject } from "../../contexts/ProjectContext";
import { useEnvironmentConfigContext } from "../../contexts/EnvironmentConfigContext";
import { useHighlightedPoles } from "../../contexts/HighlightedPolesContext";
import { useQueryResults } from "../../contexts/QueryResultsContext";
import { useTabContext } from "../../contexts/ReportTabContext";
import { useUser } from "../../contexts/UserContext";
import SaveQueryPopup from "../../global_components/save_query_popup.js";
import {
  usePoleExportData,
  getFormattedFileName,
  executeExcelExport,
} from "../../global_components/poleExportHelper";

function ReliabilityReportPage() {
  //states to hold the categories from the webapi
  //const [rejectStatusList, setRejectStatusList] = useState([]);
  const [inspectionConditionList, setInspectionConditionList] = useState([]);
  const [notRestoredReasonList, setNotRestoredReasonList] = useState([]);
  const [savedQueryList, setSavedQueryList] = useState([]);
  //const [feederList, setFeederList] = useState([]);

  //states to hold values for searching
  const [reportType, setReportType] = useState("Inspection");
  const [reportDropdownValue, setReportDropdownValue] = useState({
    id: 1,
    name: "Inspection",
  });
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [structureNumber, setStructureNumber] = useState("");
  const [startAuditDate, setStartAuditDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [circuitId, setCircuitId] = useState("");
  const [endAuditDate, setEndAuditDate] = useState(null);
  const [dynamicSchema, setDynamicSchema] = useState([]);
  const [dynamicFormValues, setDynamicFormValues] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [searchedOptions, setSearchedOptions] = useState({});
  const lastValidQuery = useRef("");
  const searchDebounceTimer = useRef({});
  const [loadingColumns, setLoadingColumns] = useState({});

  //utility states / other utilities
  const { user } = useUser();
  const {
    queryResults,
    setQueryResults,
    isQueryActive,
    activeQueryPayload,
    setActiveQueryPayload,
  } = useQueryResults();
  const { selectedProject, GCP_Project, Dataset_ID } = useProject();
  const { highlightedPoles, setHighlightedPoles } = useHighlightedPoles();
  const [notRestoredReason, setNotRestoredReason] = useState("");
  const { selectedTab, setSelectedTab } = useTabContext();
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [allExpanded, setAllExpanded] = useState();
  const [dataState, setDataState] = useState({ skip: 0, take: 10 });
  const [dataResult, setDataResult] = useState(
    process(queryResults, dataState),
  );
  const [showSaveQueryPopup, setShowSaveQueryPopup] = useState(false);
  const [saveQueryOptions, setSaveQueryOptions] = useState(null);
  const [saveQueryError, setSaveQueryError] = useState("");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const min = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  const excelExportRef = useRef(null);
  const gridWidth = 600;
  const navigate = useNavigate();
  const { environmentConfig } = useEnvironmentConfigContext();

  const handleDynamicFieldChange = (columnName, value) => {
    setDynamicFormValues((prev) => ({
      ...prev,
      [columnName]: value,
    }));
  };

  //returns bool if there is at least one valid entry
  function isValidSubmit() {
    const hasDynamicValue = Object.values(dynamicFormValues).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      return val != null && val !== "";
    });

    if (reportType === "Inspection") {
      return (
        hasDynamicValue ||
        isValidEntry(startAuditDate) ||
        isValidEntry(endAuditDate)
      );
    } else if (reportType === "Restoration") {
      return (
        hasDynamicValue || isValidEntry(startDate) || isValidEntry(endDate)
      );
    }
    return false;
  }

  function isValidEntry(entry) {
    return entry !== null && entry !== undefined && entry !== "";
  }

  function resetQueryOptions() {
    setDynamicFormValues({});
    setStructureNumber("");
    setStartAuditDate(null);
    setEndAuditDate(null);
    setStartDate(null);
    setEndDate(null);
    setCircuitId("");
    setNotRestoredReason("");
  }

  function clearAllQueryFields() {
    resetQueryOptions();
    setSelectedQuery(null);
    setActiveQueryPayload(""); // Clear context payload cache
  }

  // MAPPING THE POLE / REPORT TYPE TO EXPECTED VERBIAGE FOR BQ
  const mappedType =
    reportType === "Inspection"
      ? "Inspection Pole"
      : reportType === "Restoration"
        ? "Restoration Pole"
        : "";

  function buildSelectedQueryOptions() {
    const dynamicFiltersPayload = {};
    Object.keys(dynamicFormValues).forEach((key) => {
      const val = dynamicFormValues[key];
      if (val instanceof Date) {
        const localDate = new Date(
          val.getTime() - val.getTimezoneOffset() * 60000,
        );
        dynamicFiltersPayload[key] = localDate.toISOString().split("T")[0];
      } else if (Array.isArray(val) && val.length > 0) {
        dynamicFiltersPayload[key] = val
          .map((item) => `"${String(item).trim()}"`)
          .join(",");
      } else if (typeof val === "string" && val.trim() !== "") {
        dynamicFiltersPayload[key] = val
          .split(",")
          .map((item) => `"${item.trim()}"`)
          .filter((item) => item !== '""')
          .join(",");
      }
    });

    return {
      Type: mappedType,
      StartDateAudit:
        reportType === "Inspection" && startAuditDate
          ? startAuditDate.toISOString()
          : "",
      EndDateAudit:
        reportType === "Inspection" && endAuditDate
          ? endAuditDate.toISOString()
          : "",
      RestorationStartDate:
        reportType === "Restoration" && startDate
          ? startDate.toISOString()
          : "",
      RestorationEndDate:
        reportType === "Restoration" && endDate ? endDate.toISOString() : "",
      Pull_Type: "",
      DynamicFilters: dynamicFiltersPayload,
    };
  }

  function fetchQueryResults(payload) {
    setLoading(true);
    try {
      fetch(
        process.env.REACT_APP_WEB_API_URL +
          "/api-common/Secrets/GetNimblyWebAPIKey",
        {
          method: "GET",
          headers: {
            frontendenvironmentconfig: environmentConfig,
            "Content-Type": "application/json",
          },
        },
      )
        .then((res) => res.text())
        .then((apiKey) => {
          fetch(
            process.env.REACT_APP_WEB_API_URL + "/api-common/poles/GetRSPoles",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ApiKey: apiKey,
                frontendenvironmentconfig: environmentConfig,
              },
              body: payload,
            },
          )
            .then(async (res) => {
              if (!res.ok) {
                const errText = await res.text();
                throw new Error(errText);
              }
              return res.json();
            })
            .then((data) => {
              const flattenedData = data.map((item) => ({
                GlobalId: item.GlobalId,
                fieldDict: item.fieldDict,
                photosDict: item.photosDict,
              }));
              setSelectedTab(1);
              setQueryResults(flattenedData);
              setDataState({ skip: 0, take: 10 });
              setAllExpanded(false);
              setLoading(false);
            })
            .catch((err) => {
              console.error("Backend Error:", err);
              setLoading(false);
            });
        });
    } catch (ex) {
      console.error("Error executing query:", ex);
      setLoading(false);
    }
  }

  function populateDropdownData() {
    setLoading(true);
    try {
      fetch(
        process.env.REACT_APP_WEB_API_URL +
          "/api-common/Secrets/GetNimblyWebAPIKey",
        {
          method: "GET",
          headers: {
            frontendenvironmentconfig: environmentConfig,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        },
      )
        .then((res) => {
          return res.text();
        })
        .then((apiKey) => {
          fetch(
            process.env.REACT_APP_WEB_API_URL +
              "/api-common/poles/GetDynamicQuerySchema",
            {
              method: "POST",
              headers: {
                frontendenvironmentconfig: environmentConfig,
                "Content-Type": "application/json",
                ApiKey: apiKey,
              },
              body: JSON.stringify({
                rawQuery: "",
                Project: selectedProject,
                GCP_Project: GCP_Project,
                Dataset_ID: Dataset_ID,
                Type: mappedType,
                Pull_Type: "",
                DynamicFilters: {},
              }),
            },
          )
            .then((res) => {
              if (!res.ok) return res.text().then((text) => ({ error: text }));
              return res.json();
            })
            .then((schemaData) => {
              if (Array.isArray(schemaData)) {
                setDynamicSchema(schemaData);
              } else {
                console.error(
                  "El backend devolvió un error en lugar de la lista:",
                  schemaData,
                );
                setDynamicSchema([]);
              }
              setLoading(false);
            });
        });
    } catch (ex) {
      console.error("Error fetching rs categories", ex);
      setLoading(false);
    }
  }

  const flattenObject = (obj, parentKey = "", result = {}) => {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (
          obj[key] &&
          typeof obj[key] === "object" &&
          !Array.isArray(obj[key])
        ) {
          flattenObject(obj[key], newKey, result);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  };

  // Pull export memoized data configurations directly from the helper hook
  const { exportData, columns } = usePoleExportData(queryResults);

  const exportRows = () => {
    executeExcelExport(excelExportRef, exportData);
  };

  function submitQuery() {
    if (!isValidSubmit()) {
      setShowWarning(true);
      return;
    }
    setShowWarning(false);

    const dynamicFiltersPayload = {};
    Object.keys(dynamicFormValues).forEach((key) => {
      const val = dynamicFormValues[key];
      if (val instanceof Date) {
        const localDate = new Date(
          val.getTime() - val.getTimezoneOffset() * 60000,
        );
        dynamicFiltersPayload[key] = localDate.toISOString().split("T")[0];
      } else if (Array.isArray(val) && val.length > 0) {
        dynamicFiltersPayload[key] = val.join(",");
      } else if (typeof val === "string" && val.trim() !== "") {
        dynamicFiltersPayload[key] = val.trim();
      }
    });

    const newPayload = JSON.stringify({
      Type: mappedType,
      Project: selectedProject,
      GCP_Project: GCP_Project,
      Dataset_ID: Dataset_ID,
      StartDateAudit:
        reportType === "Inspection" && startAuditDate
          ? startAuditDate.toISOString()
          : "",
      EndDateAudit:
        reportType === "Inspection" && endAuditDate
          ? endAuditDate.toISOString()
          : "",
      RestorationStartDate:
        reportType === "Restoration" && startDate
          ? startDate.toISOString()
          : "",
      RestorationEndDate:
        reportType === "Restoration" && endDate ? endDate.toISOString() : "",
      Pull_Type: "",
      DynamicFilters: dynamicFiltersPayload,
    });
    setActiveQueryPayload(newPayload);
    fetchQueryResults(newPayload);
  }

  function expandChange(event) {
    const isExpanded =
      event.dataItem.expanded === undefined
        ? event.dataItem.aggregates
        : event.dataItem.expanded;
    event.dataItem.expanded = !isExpanded;
    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
  }

  function expandAllRows(shouldExpand) {
    let updatedExpandStatus = !allExpanded;
    let newData = queryResults.map((item) => {
      item.expanded = updatedExpandStatus;
      return item;
    });
    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
    setAllExpanded(!allExpanded);
  }

  const viewInMap = () => {
    const highlightedPolesList = queryResults.map((item) => item.GlobalId);
    setHighlightedPoles(highlightedPolesList);
    const updatedPoleData = queryResults.map((item) => ({
      ...item,
      highlighted: highlightedPolesList.includes(item.GlobalId),
    }));

    navigate("/map");
  };

  function dataStateChange(event) {
    setDataResult(process(queryResults, event.dataState));
    setDataState(event.dataState);
  }

  function setPercentage(percentage) {
    return Math.round(gridWidth / 100) * percentage;
  }

  function openSaveQueryPopup() {
    if (!isValidSubmit()) {
      setShowWarning(true);
      return;
    }

    setShowWarning(false);
    setSaveQueryError("");
    const options = buildSelectedQueryOptions();
    setSaveQueryOptions(options);
    setShowSaveQueryPopup(true);
  }

  function closeSaveQueryPopup() {
    setSaveQueryError("");
    setShowSaveQueryPopup(false);
  }

  function handleSaveQueryConfirm({ queryName, queryScope, queryOptions }) {
    const payloadToSave = {
      QueryName: queryName,
      QueryScope: queryScope,
      QueryPage: "ReliabilityReport",
      ReportType: reportType,
      Project: selectedProject,
      GCP_Project: GCP_Project,
      Dataset_ID: Dataset_ID,
      UserEmail: user?.email || "",
      QueryOptions: queryOptions,
    };

    setSaveQueryError("");

    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.text())
      .then((apiKey) => {
        return fetch(
          process.env.REACT_APP_WEB_API_URL + "/api-common/poles/SaveQuery",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ApiKey: apiKey,
              frontendenvironmentconfig: environmentConfig,
            },
            body: JSON.stringify(payloadToSave),
          },
        );
      })
      .then(async (res) => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(errorText || "Failed to save query.");
        }
        return res.json();
      })
      .then((data) => {
        setSaveQueryError("");
        setShowSaveQueryPopup(false);
        fetchSavedQueries();
        setShowSaveSuccess(true);
        setTimeout(() => {
          setShowSaveSuccess(false);
        }, 3000);
      })
      .catch((err) => {
        console.error("Error saving query:", err);

        let errorMessage = "Failed to save query.";

        if (err.message) {
          if (err.message.includes("already exists")) {
            errorMessage =
              "A saved query with this name already exists for this project.";
          } else {
            errorMessage = err.message;
          }
        }

        setSaveQueryError(errorMessage);
      });
  }

  function fetchSavedQueries() {
    if (!selectedProject || !Dataset_ID || !user?.email) return;

    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.text())
      .then((apiKey) => {
        return fetch(
          process.env.REACT_APP_WEB_API_URL +
            "/api-common/poles/GetSavedQueries",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ApiKey: apiKey,
              frontendenvironmentconfig: environmentConfig,
            },
            body: JSON.stringify({
              QueryPage: "ReliabilityReport",
              Project: selectedProject,
              Dataset_ID: Dataset_ID,
              UserEmail: user.email,
            }),
          },
        );
      })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch saved queries.");
        }
        return res.json();
      })
      .then((data) => {
        setSavedQueryList(data || []);
      })
      .catch((err) => {
        console.error("Error fetching saved queries:", err);
      });
  }

  function handleSavedQueryChange(e) {
    const selected = e.target.value;
    setSelectedQuery(selected);

    if (!selected) return;

    const options = selected.QueryOptions || {};
    let nextReportType = selected.ReportType || "Inspection";

    if (nextReportType === "Pole Inspection") nextReportType = "Inspection";
    if (nextReportType === "Pole Restoration") nextReportType = "Restoration";

    resetQueryOptions();
    setReportType(nextReportType);

    const reportOption =
      nextReportType === "Inspection"
        ? { id: 1, name: "Inspection" }
        : { id: 2, name: "Restoration" };

    setReportDropdownValue(reportOption);

    const parsedDynamicFilters = {};
    if (options.DynamicFilters) {
      Object.keys(options.DynamicFilters).forEach((key) => {
        const val = options.DynamicFilters[key];
        if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
          parsedDynamicFilters[key] = new Date(
            val.includes("T") ? val : `${val}T00:00:00`,
          );
        } else if (
          typeof val === "string" &&
          (val.includes('"') || val.includes(","))
        ) {
          parsedDynamicFilters[key] = val
            .split(",")
            .map((item) => item.replace(/^["']|["']$/g, "").trim())
            .filter((item) => item !== "");
        } else if (
          typeof val === "string" &&
          val.startsWith('"') &&
          val.endsWith('"')
        ) {
          parsedDynamicFilters[key] = [val.replace(/^["']|["']$/g, "").trim()];
        } else {
          parsedDynamicFilters[key] = val;
        }
      });
    }
    setDynamicFormValues(parsedDynamicFilters);

    if (nextReportType === "Inspection") {
      setStartAuditDate(
        options.StartDateAudit ? new Date(options.StartDateAudit) : null,
      );
      setEndAuditDate(
        options.EndDateAudit ? new Date(options.EndDateAudit) : null,
      );
    } else if (nextReportType === "Restoration") {
      setStartDate(
        options.RestorationStartDate
          ? new Date(options.RestorationStartDate)
          : null,
      );
      setEndDate(
        options.RestorationEndDate
          ? new Date(options.RestorationEndDate)
          : null,
      );
      setNotRestoredReason(options.NotRestoredReason || "");
    }
  }

  useEffect(() => {
    if (selectedProject) {
      populateDropdownData();
      if (!selectedQuery) {
        resetQueryOptions();
        setSelectedQuery(null);
      }
      setDataResult(process([], dataState));
    }
    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
  }, [selectedProject, reportType, selectedQuery]);

  useEffect(() => {
    if (selectedProject && Dataset_ID && user?.email) {
      fetchSavedQueries();
    } else {
      setSavedQueryList([]);
      setSelectedQuery(null);
    }
  }, [selectedProject, Dataset_ID, user?.email, environmentConfig]);

  useEffect(() => {
    const allRowsExpanded =
      queryResults.length > 0 && queryResults.every((item) => item.expanded);
    setAllExpanded(allRowsExpanded);
  }, [queryResults]);

  useEffect(() => {
    setDataResult(process(queryResults, dataState));
  }, [queryResults]);

  //detail components for the datagrid
  function DetailComponent(props) {
    const dataItem = props.dataItem;
    let last = 3;
    let first = 1;
    const picCol =
      dataItem.photosDict && Object.keys(dataItem.photosDict).length > 0
        ? first++
        : last--;
    const locationCol = first++;
    const infoCol = first++;
    const renderFieldItems = (fields) => {
      return Object.entries(fields).map(([key, value], index) => (
        <React.Fragment key={index}>
          <GridLayoutItem
            className="pulse-queryresult-expanded-detail-column"
            row={index + 2}
            col={1}
          >
            {key.replace(/_/g, " ")}:
          </GridLayoutItem>
          <GridLayoutItem row={index + 2} col={2}>
            {value}
          </GridLayoutItem>
        </React.Fragment>
      ));
    };

    function Photos() {
      return (
        <GridLayoutItem className="pulse-img-container" row={1} col={picCol}>
          {dataItem.photosDict &&
            Object.values(dataItem.photosDict).map((photo, x) => (
              <a
                href={photo["Photo Path"]}
                target="_blank"
                rel="noopener noreferrer"
                key={x}
              >
                <img
                  className="pulse-img"
                  src={photo["Photo Path"]}
                  alt={`Photo ${x + 1}`}
                />
              </a>
            ))}
        </GridLayoutItem>
      );
    }

    function Info() {
      return (
        <GridLayoutItem
          className="pulse-expand-content pulse-img-container"
          row={1}
          col={infoCol}
        >
          <GridLayout
            gap={{ rows: 14, cols: 2 }}
            cols={[{ width: "49%" }, { width: "49%" }]}
          >
            <GridLayoutItem
              className="pulse-expand-title"
              row={1}
              col={1}
              colSpan={2}
            >
              Information
            </GridLayoutItem>
            {renderFieldItems(dataItem.fieldDict.Poles)}
          </GridLayout>
        </GridLayoutItem>
      );
    }

    function Location() {
      return (
        <GridLayoutItem
          className="pulse-expand-content pulse-img-container"
          row={1}
          col={locationCol}
        >
          <GridLayout
            gap={{ rows: 8, cols: 2 }}
            cols={[{ width: "49%" }, { width: "49%" }]}
          >
            <GridLayoutItem
              className="pulse-expand-title"
              row={1}
              col={1}
              colSpan={2}
            >
              Location Details
            </GridLayoutItem>
            {renderFieldItems(dataItem.fieldDict.Location_Details)}
          </GridLayout>
        </GridLayoutItem>
      );
    }

    return (
      <GridLayout
        gap={{ rows: 2, cols: 4 }}
        rows={[{ height: 250 }]}
        cols={[
          { width: "25%" },
          { width: "25%" },
          { width: "25%" },
          { width: "25%" },
        ]}
      >
        {dataItem.photosDict && Object.keys(dataItem.photosDict).length > 0 ? (
          <Photos />
        ) : null}
        <Info />
        <Location />
      </GridLayout>
    );
  }

  const handleFilterChange = (columnName, filterValue) => {
      const safeText = (filterValue ?? "").toString();
  
      setColumnFilters((prev) => ({
        ...prev,
        [columnName]: safeText,
      }));
  
      if (searchDebounceTimer.current[columnName]) {
        clearTimeout(searchDebounceTimer.current[columnName]);
      }
  
      const trimmed = safeText.trim();
  
      const isGlobalIdCol = columnName.toLowerCase().includes("globalid");
      const minLengthRequired = isGlobalIdCol ? 4 : 2;
  
      if (trimmed.length < minLengthRequired) {
        setSearchedOptions((prev) => ({
          ...prev,
          [columnName]: [],
        }));
        setLoadingColumns((prev) => ({ ...prev, [columnName]: false }));
        return;
      }
  
      const waitTime = trimmed.length > 8 ? 300 : 950;

      setLoadingColumns((prev) => ({ ...prev, [columnName]: true }));

  
      searchDebounceTimer.current[columnName] = setTimeout(() => {

          fetch(
                process.env.REACT_APP_WEB_API_URL +
                  "/api-common/Secrets/GetNimblyWebAPIKey",
                {
                  method: "GET",
                  headers: {
                    frontendenvironmentconfig: environmentConfig,
                    "Content-Type": "application/json",
                  },
                }
              )
                .then((res) => res.text())
                .then((apiKey) => {
                  return fetch(
                    process.env.REACT_APP_WEB_API_URL +
                      "/api-common/poles/SearchColumnOptions",
                    {
                      method: "POST",
                      headers: {
                        frontendenvironmentconfig: environmentConfig,
                        "Content-Type": "application/json",
                        ApiKey: apiKey,
                      },
                      body: JSON.stringify({
                        GCP_Project: GCP_Project,
                        TableName: "Poles", 
                        ColumnName: columnName,
                        SearchText: trimmed,
                        Type: mappedType,
                      }),
                    }
                  );
                })
         .then(async (res) => {
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(errorText || "Failed to search column options.");
          }
          return res.json();
        })
            .then((matches) => {
              if (!Array.isArray(matches)) return;
              setSearchedOptions((prev) => ({
                ...prev,
                [columnName]: matches
                  .filter((m) => m != null && m !== "")
                  .map(String),
              }));
            })
            .catch((err) => {
              console.error("Error looking for options:", err);
              setSearchedOptions((prev) => ({
                ...prev,
                [columnName]: [],
              }));
            })
            .finally(() => {
              setLoadingColumns((prev) => ({ ...prev, [columnName]: false })); 
            })
    }, waitTime);
  };

  const Warning = (
    <div className="loading-icon-container">
      <img className="nimbly2-loader-gif" src={loadinggif} />{" "}
    </div>
  );

  return (
    <div className="ken-nimbly-page-content-wrapper">
      {loading ? Warning : null}
      <TabStrip
        selected={selectedTab}
        onSelect={(e) => setSelectedTab(e.selected)}
      >
        <TabStripTab title="QUERY WIZARD">
          <p className="ken-nimbly-ov-main-title">Query Wizard</p>
          <div className="pulse-wizard-container">
            <div className="pulse-wizard-container-header">Generate Query</div>
            <div
              className={
                !showWarning
                  ? "pulse-warning-no-display"
                  : "pulse-wizard-container-warning"
              }
            >
              Please fill in at least one field to continue
            </div>
            <div
              className={
                !showSaveSuccess
                  ? "pulse-warning-no-display"
                  : "pulse-wizard-container-success"
              }
            >
              {" "}
              Query saved successfully
            </div>
            <div className="pulse-wizard-container-2">
              <GridLayout gap={{ rows: 9, cols: 2 }} cols={[{ width: 150 }]}>
                {/* --- SELECT A QUERY --- */}
                <GridLayoutItem row={1} col={1} colSpan={7}>
                  <p className="Pulse-report-form-title">APPLY A SAVED QUERY</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>

                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={2}
                  col={1}
                >
                  Query:
                </GridLayoutItem>
                <GridLayoutItem row={2} col={2} colSpan={6}>
                  <DropDownList
                    data={savedQueryList}
                    textField="QueryName"
                    dataItemKey="Id"
                    value={selectedQuery}
                    onChange={(e) => handleSavedQueryChange(e)}
                  />
                </GridLayoutItem>

                {/* --- SELECT REPORT --- */}
                <GridLayoutItem row={3} col={1} colSpan={7}>
                  <p className="Pulse-report-form-title">SELECT REPORT</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>

                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={4}
                  col={1}
                >
                  Report Type:
                </GridLayoutItem>
                <GridLayoutItem row={4} col={2} colSpan={6}>
                  <DropDownList
                    data={[
                      { id: 1, name: "Inspection" },
                      { id: 2, name: "Restoration" },
                    ]}
                    textField="name"
                    dataItemKey="id"
                    value={reportDropdownValue}
                    onChange={(e) => {
                      const reportValue = e.target.value.name;
                      const reportDropdownSelectedValue = e.target.value;
                      resetQueryOptions();
                      setSelectedQuery(null);
                      setReportType(reportValue);
                      setReportDropdownValue(reportDropdownSelectedValue);
                    }}
                  />
                </GridLayoutItem>

                {reportType === "Restoration" ? (
                  <>
                    <GridLayoutItem row={5} col={1} colSpan={7}>
                      <p className="Pulse-report-form-title">
                        RESTORATION DATE
                      </p>
                      <div className="box-divider"></div>
                    </GridLayoutItem>
                    <GridLayoutItem
                      className="pulse-querywizard-column-text"
                      row={6}
                      col={1}
                    >
                      Start Date:
                    </GridLayoutItem>
                    <GridLayoutItem row={6} col={2}>
                      <DatePicker
                        min={min}
                        value={startDate}
                        placeholder="Choose a date..."
                        onChange={(e) => setStartDate(e.value)}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      className="pulse-querywizard-column-text"
                      row={6}
                      col={3}
                    >
                      End Date:
                    </GridLayoutItem>
                    <GridLayoutItem row={6} col={4}>
                      <DatePicker
                        min={min}
                        value={endDate}
                        placeholder="Choose a date..."
                        onChange={(e) => setEndDate(e.value)}
                      />
                    </GridLayoutItem>
                  </>
                ) : (
                  <>
                    <GridLayoutItem row={5} col={1} colSpan={7}>
                      <p className="Pulse-report-form-title">AUDIT DATE</p>
                      <div className="box-divider"></div>
                    </GridLayoutItem>
                    <GridLayoutItem
                      className="pulse-querywizard-column-text"
                      row={6}
                      col={1}
                    >
                      Start Date:
                    </GridLayoutItem>
                    <GridLayoutItem row={6} col={2}>
                      <DatePicker
                        min={min}
                        value={startAuditDate}
                        placeholder="Choose a date..."
                        onChange={(e) => setStartAuditDate(e.value)}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      className="pulse-querywizard-column-text"
                      row={6}
                      col={3}
                    >
                      End Date:
                    </GridLayoutItem>
                    <GridLayoutItem row={6} col={4}>
                      <DatePicker
                        min={min}
                        value={endAuditDate}
                        placeholder="Choose a date..."
                        onChange={(e) => setEndAuditDate(e.value)}
                      />
                    </GridLayoutItem>
                  </>
                )}

                <GridLayoutItem row={7} col={1} colSpan={7}>
                  <p className="Pulse-report-form-title">PROJECT INFO</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>

                {Array.isArray(dynamicSchema) &&
                  dynamicSchema.map((field, index) => {
                    const rowNumber = 8 + index;
                    const colName = field.columnName || field.ColumnName || "";
                    const isNumber =
                      field.isNumberField || field.IsNumberField || false;
                    const isDate =
                      field.isDateField ||
                      field.IsDateField ||
                      colName.toLowerCase().includes("date");
                    const colOptions = (field.options || field.Options || [])
                      .filter((o) => o != null && o !== "")
                      .map(String);

                    const rawSelected = dynamicFormValues[colName];
                    const selectedArray = Array.isArray(rawSelected)
                      ? rawSelected
                      : typeof rawSelected === "string" &&
                          rawSelected.trim() !== ""
                        ? rawSelected
                            .split(",")
                            .map((v) => v.replace(/^["']|["']$/g, "").trim())
                            .filter(Boolean)
                        : [];
                    const selectedValues = selectedArray
                      .filter((v) => v != null && v !== "")
                      .map(String);

                    const searchText = (columnFilters[colName] || "")
                      .trim()
                      .toLowerCase();

                    const isSearching = searchText.length >= 2;
                      
                    const isFetching = !!loadingColumns[colName];
                    let activeOptions = colOptions;

                    if (isSearching) {
                      const remoteResults = searchedOptions[colName];
                      if (remoteResults && remoteResults.length > 0) {
                        activeOptions = remoteResults.filter((opt) =>
                          opt.toLowerCase().includes(searchText),
                        );
                      } else {
                        activeOptions = colOptions.filter((opt) =>
                          opt.toLowerCase().includes(searchText),
                        );
                      }
                    }
                    const displayedOptions = activeOptions.filter(
                      (opt) => !selectedValues.includes(opt),
                    );

                    const noDataMessage = isFetching ? "Searching..." : "No data found";

                    return (
                      <React.Fragment key={colName}>
                        <GridLayoutItem
                          className="pulse-querywizard-column-text"
                          row={rowNumber}
                          col={1}
                        >
                          {colName.replace(/_/g, " ")}:
                        </GridLayoutItem>
                        <GridLayoutItem row={rowNumber} col={2} colSpan={6}>
                          {isDate ? (
                            <DatePicker
                              value={dynamicFormValues[colName] || null}
                              onChange={(e) =>
                                handleDynamicFieldChange(colName, e.value)
                              }
                              placeholder="Choose a date..."
                            />
                          ) : isNumber ? (
                            <Input
                              value={dynamicFormValues[colName] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(colName, e.value)
                              }
                              placeholder="Ej: 123, 456, 789"
                            />
                          ) : (
                            <MultiSelect
                              data={displayedOptions}
                              value={dynamicFormValues[colName] || []}
                              onChange={(e) => {
                                handleDynamicFieldChange(colName, e.value || []);
                                setColumnFilters((prev) => ({ ...prev, [colName]: "" }));
                                setSearchedOptions((prev) => ({ ...prev, [colName]: [] }));
                              }}
                              filterable={true}
                              onFilterChange={(e) =>
                                handleFilterChange(
                                  colName,
                                  e.filter?.value ?? "",
                                )
                              }
                              placeholder="Select an option..."
                              listNoDataRender={() => (
                                <div
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    color: "#666",
                                  }}
                                >
                                  {noDataMessage}
                                </div>
                              )}
                            />
                          )}
                        </GridLayoutItem>
                      </React.Fragment>
                    );
                  })}

                <GridLayoutItem
                  className="pulse-textalignment-center"
                  row={8 + (dynamicSchema?.length || 0)}
                  col={2}
                >
                  <Button
                    type={"button"}
                    className="pulse-wizard-save-button"
                    onClick={openSaveQueryPopup}
                  >
                    Save Query
                  </Button>
                  <Button
                    type={"button"}
                    className="pulse-wizard-save-button"
                    onClick={clearAllQueryFields}
                  >
                    Clear All
                  </Button>
                  <Button
                    themeColor={"primary"}
                    type={"submit"}
                    className="pulse-wizard-submit-button"
                    onClick={submitQuery}
                  >
                    Execute Query
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </div>
          </div>
        </TabStripTab>
        <TabStripTab title="QUERY REPORT">
          <div className="pulse-query-wizard-top-row">
            <p className="ken-nimbly-ov-main-title">Query Report</p>
            <div className="report-action-btn-group">
              <Button
                onClick={() => fetchQueryResults(activeQueryPayload)}
                disabled={!isQueryActive}
                className="pulse-expand-collapse-report-button"
              >
                <span>Get Latest Data</span>
              </Button>
              <Button
                onClick={() => viewInMap()}
                disabled={!isQueryActive}
                className="pulse-expand-collapse-report-button"
              >
                <span>View Results in Map</span>
              </Button>
              <Button
                onClick={() => expandAllRows()}
                disabled={queryResults.length === 0}
                className="pulse-expand-collapse-report-button"
              >
                <span>{allExpanded ? "Collapse" : "Expand"} All</span>
              </Button>
              <Button
                onClick={exportRows}
                disabled={queryResults.length === 0}
                className="pulse-expand-collapse-report-button"
              >
                <span>Export</span>
              </Button>
            </div>
          </div>
          <div>
            <div className="pulse-wizard-container-header">Query Results</div>
            <ExcelExport
              key="reliability-report-excel-exporter"
              ref={excelExportRef}
              data={exportData}
              columns={columns}
              fileName={getFormattedFileName(selectedProject)}
              // Fixes the double-clicking layout glitch by converting strings to executable code
              onWorkbookCreate={(e) => {
                e.workbook.sheets.forEach((sheet) => {
                  sheet.rows.forEach((row) => {
                    if (row.type === "data") {
                      row.cells.forEach((cell) => {
                        if (
                          typeof cell.value === "string" &&
                          cell.value.startsWith("=")
                        ) {
                          cell.formula = cell.value.substring(1);
                          cell.value = null;
                        }
                      });
                    }
                  });
                });
              }}
              rowOptions={{
                headerRowHeight: 30,
                dataRowHeight: 24,
              }}
            />
            <Grid
              data={dataResult}
              {...dataState}
              onDataStateChange={dataStateChange}
              detail={DetailComponent}
              style={{
                height: "100%",
              }}
              expandField="expanded"
              onExpandChange={expandChange}
              filterable={true}
              pageable={{
                buttonCount: 4,
                pageSizes: [5, 10, 15, 20],
              }}
            >
              <Column
                field="fieldDict.Poles.Structure ID"
                title="Structure ID"
                width={setPercentage(55)}
              />
              <Column
                field="fieldDict.Poles.Audit Date"
                title="Audit Date"
                width={setPercentage(55)}
              />
            </Grid>
          </div>
        </TabStripTab>
      </TabStrip>
      {showSaveQueryPopup && (
        <SaveQueryPopup
          queryOptions={saveQueryOptions}
          saveError={saveQueryError}
          onClose={closeSaveQueryPopup}
          onConfirm={handleSaveQueryConfirm}
        />
      )}
    </div>
  );
}

export default ReliabilityReportPage;
